<template>
  <Section
    :id="id"
    :header="t('glCodes.glCodes', 2)"
    class="gl-code-section"
    data-class="flex flex-gap-1 flex-column"
  >
    <div class="gl-code-section-gl-codes flex flex-gap-1 flex-wrap">
      <GLCodeInlineEdit
        v-for="(glCode, index) in sortedGLCodes"
        :key="glCode.code"
        :model-value="glCode"
        :options="availableGLCodeOptions"
        :editable="editable"
        @remove="onRemove"
        @update:model-value="onUpdate(index, $event)"
      />
    </div>

    <div v-if="newGLCode" class="gl-code-section-new flex">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId }"
        :input-id="`${id}-new-code`"
        :label="t('glCodes.glCodes')"
        class="w-6"
      >
        <GLCodeAutocomplete
          :id="inputId"
          v-model="newGLCode"
          v-bind="ariaProps"
          :class="inputClasses"
          :gl-codes="availableGLCodeOptions"
          :placeholder="t('glCodes.glCodes')"
          @keydown.enter="onSubmit"
          @keydown.esc="onCancel"
        />
      </InputWrapper>
    </div>

    <div
      v-if="newGLCode"
      class="gl-code-section-buttons flex flex-gap-1 justify-content-end"
    >
      <RygenButton
        mode="tertiary"
        :label="t('common.remove')"
        icon="pi pi-times"
        icon-pos="left"
        @click="onCancel"
      />
      <RygenButton
        mode="tertiary"
        :disabled="!newGLCode.code"
        :label="t('common.save')"
        icon="pi pi-check"
        icon-pos="left"
        @click="onSubmit"
      />
    </div>

    <div
      v-if="autoGlCodeSuggestion && !hasAutoGLCode"
      class="auto-gl-code-suggestion flex flex-column flex-gap-1"
    >
      <p class="text-gray-darker">
        {{ t('glCodes.autoGlCodingDisabled') }}
      </p>

      <RygenButton
        class="suggestion-button align-self-end"
        mode="tertiary"
        :label="t('common.useSuggestedParam', { param: autoGlCodeSuggestion })"
        @click="onAcceptSuggestion"
      />
    </div>

    <div
      v-if="!newGLCode && editable"
      class="gl-code-section-buttons flex flex-gap-1 justify-content-end"
    >
      <AddButton :label="t('glCodes.glCodes')" @click="onAdd" />
    </div>
  </Section>
</template>

<script lang="ts" setup>
import isEqual from 'lodash/isEqual';
import { computed, PropType, Ref, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useDirtyState } from '@rygen/rygen-ui/composables';
import { DomainReference, GLCode } from '@rygen/rygen-ui/model';
import { alphabeticComparator, notNull } from '@rygen/rygen-ui/util';

import { useGLCodeStore } from '@/stores/GLCode';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { glCodeComparator } from '@/util';

import GLCodeAutocomplete from '@/components/glCode/GLCodeAutocomplete.vue';
import GLCodeInlineEdit from '@/components/glCode/GLCodeInlineEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: Array as PropType<GLCode[]>,
    default: () => [],
  },
  autoGlCodeSuggestion: {
    type: String,
    default: () => '',
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, modelValue, autoGlCodeSuggestion, editable } = toRefs(props);
const emit = defineEmits<{
  (
    event: 'change',
    dirtyState: {
      isDirty: boolean;
      numDirty: number;
    },
  ): void;
  (event: 'update:model-value', newVal: GLCode[]): void;
}>();

const { dirtyState, itemAdded, itemDeleted, itemEdited, resetDirtyState } =
  useDirtyState(modelValue.value);
const { availableGLCodes, loadGLCodes } = useGLCodeStore();
const { t } = useI18n();
const { owningClient } = useParentEntityStore();

const internalValue: Ref<GLCode[]> = ref(modelValue.value);
const totalGLCodes = ref(modelValue.value.length);
const newGLCode: Ref<GLCode | null> = ref(null);

const sortByName = alphabeticComparator({ isCaseSensitive: false });

const hasAutoGLCode = computed(() => {
  return internalValue.value.some((g) => g.autoGLCode);
});

const sortedGLCodes = computed(() => {
  return internalValue.value.toSorted(glCodeComparator);
});

// Filter out already used types
const availableGLCodeOptions = computed(() => {
  return (
    availableGLCodes.value.data
      ?.filter((clientType) => {
        const existingGLCode = internalValue.value.find(
          (g) => g.code && g.code === clientType.code,
        );
        return !existingGLCode;
      })
      ?.filter(notNull)
      ?.sort((a: GLCode, b: GLCode) => sortByName(a.code, b.code)) ?? []
  );
});

const onUpdate = (index: number, glCode: GLCode) => {
  // `index` is the index into `sortedGLCodes`; convert it to an index into
  // `internalValue`/`modelValue`
  const originalGLCode = sortedGLCodes.value[index];
  const originalIndex = internalValue.value.findIndex(
    (g) => g.code === originalGLCode.code,
  );

  itemEdited(originalIndex, !isEqual(glCode, modelValue.value[originalIndex]));
  internalValue.value.splice(originalIndex, 1, glCode);
};

const onAdd = () => {
  newGLCode.value = new GLCode({ code: '' });
};

const onSubmit = () => {
  if (newGLCode.value?.code) {
    internalValue.value.push(newGLCode.value);
    newGLCode.value = null;
    itemAdded();
  }
};

const onRemove = (glCode: GLCode) => {
  const index = internalValue.value.findIndex((g) => g.code === glCode.code);
  if (index === -1) {
    return;
  }

  itemDeleted(index, index < totalGLCodes.value);
  internalValue.value.splice(index, 1);
};

const onCancel = () => {
  newGLCode.value = null;
};

const onAcceptSuggestion = () => {
  const newAutoGLCode = new GLCode({
    code: autoGlCodeSuggestion.value,
    description: t('glCodes.autoGlCode'),
    autoGLCode: true,
  });
  internalValue.value.push(newAutoGLCode);
  itemAdded();
};

watch(modelValue, (newVal: GLCode[]) => {
  internalValue.value = newVal;
  resetDirtyState(newVal);
  totalGLCodes.value = newVal.length;
});

watch(
  internalValue,
  (newVal: GLCode[]) => {
    emit('change', dirtyState.value);
    emit('update:model-value', newVal);
  },
  { deep: true },
);

watch(
  owningClient,
  (newVal: DomainReference | undefined) => {
    if (newVal && editable.value) {
      loadGLCodes();
    }
  },
  { immediate: true },
);
</script>
